import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Sprout, Calculator } from 'lucide-react';

const PlanteringskostnadKalkylator = () => {
  const [plantorPerHektar, setPlantorPerHektar] = useState(2500);
  const [prisPerPlanta, setPrisPerPlanta] = useState(3.5);
  const [planteringskostnad, setPlanteringskostnad] = useState(2.5);
  const [areal, setAreal] = useState(1);
  const [result, setResult] = useState(null);

  const calculateCost = () => {
    const plantorKostnad = plantorPerHektar * prisPerPlanta;
    const arbetskostnad = plantorPerHektar * planteringskostnad;
    const totalPerHektar = plantorKostnad + arbetskostnad;
    const totalKostnad = totalPerHektar * areal;
    
    setResult({
      plantorKostnad,
      arbetskostnad,
      totalPerHektar,
      totalKostnad,
      plantorTotalt: plantorPerHektar * areal
    });
  };

  return (
    <Card className="w-full" data-testid="planteringskostnad-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Sprout className="h-5 w-5" />
          Planteringskostnad kalkylator
        </CardTitle>
        <CardDescription>
          Beräkna den totala kostnaden för skogsplantering. Inkluderar plantkostnad 
          och planteringsarbete per hektar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="plantor">Plantor per hektar</Label>
            <Input
              id="plantor"
              type="number"
              value={plantorPerHektar}
              onChange={(e) => setPlantorPerHektar(Number(e.target.value))}
              min={500}
              max={5000}
              data-testid="plantor-input"
            />
            <p className="text-xs text-muted-foreground">Rekommenderat: 2000-2500 för barrträd</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="prisPlanta">Pris per planta (kr)</Label>
            <Input
              id="prisPlanta"
              type="number"
              step="0.1"
              value={prisPerPlanta}
              onChange={(e) => setPrisPerPlanta(Number(e.target.value))}
              min={0}
              data-testid="pris-planta-input"
            />
            <p className="text-xs text-muted-foreground">Typiskt: 3-5 kr för gran/tall</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="arbete">Planteringskostnad per planta (kr)</Label>
            <Input
              id="arbete"
              type="number"
              step="0.1"
              value={planteringskostnad}
              onChange={(e) => setPlanteringskostnad(Number(e.target.value))}
              min={0}
              data-testid="arbete-input"
            />
            <p className="text-xs text-muted-foreground">Typiskt: 2-3 kr/planta</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="areal">Areal (hektar)</Label>
            <Input
              id="areal"
              type="number"
              step="0.1"
              value={areal}
              onChange={(e) => setAreal(Number(e.target.value))}
              min={0.1}
              data-testid="areal-input"
            />
          </div>
        </div>

        <Button 
          onClick={calculateCost} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          Beräkna kostnad
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">Resultat</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-forest-200">
                <div>
                  <p className="text-sm text-muted-foreground">Plantkostnad/ha</p>
                  <p className="text-lg font-semibold text-forest-700">{result.plantorKostnad.toLocaleString('sv-SE')} kr</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Arbetskostnad/ha</p>
                  <p className="text-lg font-semibold text-forest-700">{result.arbetskostnad.toLocaleString('sv-SE')} kr</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total kostnad per hektar</p>
                  <p className="text-xl font-bold text-forest-700">{result.totalPerHektar.toLocaleString('sv-SE')} kr/ha</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total kostnad ({areal} ha)</p>
                  <p className="text-2xl font-bold text-forest-700">{result.totalKostnad.toLocaleString('sv-SE')} kr</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground pt-2 border-t border-forest-200">
                Totalt antal plantor: {result.plantorTotalt.toLocaleString('sv-SE')} st
              </p>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          Kostnaderna varierar beroende på markförhållanden, planttyp och region. 
          Markberedning är ej inkluderad i denna beräkning.
        </p>
      </CardContent>
    </Card>
  );
};

export default PlanteringskostnadKalkylator;
